import { useState } from 'react';
import { toast } from 'sonner';

// Declaração para gtag (Google Analytics)
declare global {
  function gtag(...args: any[]): void;
}

interface WhatsAppConfig {
  phone?: string;
  text: string;
  fallbackUrls?: string[];
}

interface WhatsAppRDOConfig {
  phone?: string;
  baseText?: string;
}

interface WhatsAppTelemetry {
  obraId?: string;
  date?: string;
  hasPhone: boolean;
  source: 'export_btn' | 'popup' | 'contact_list';
}

export const useWhatsApp = () => {
  const [isLoading, setIsLoading] = useState(false);

  // Sanitizar telefone para formato E.164
  const sanitizePhone = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');
    // Se não começa com 55 (Brasil), adicionar
    return digits.startsWith('55') ? digits : `55${digits}`;
  };

  // Construir URL do WhatsApp
  const buildWhatsAppUrl = (config: WhatsAppConfig): string => {
    const encodedText = encodeURIComponent(config.text);
    
    if (config.phone) {
      const sanitizedPhone = sanitizePhone(config.phone);
      return `https://api.whatsapp.com/send?phone=${sanitizedPhone}&text=${encodedText}`;
    } else {
      return `https://api.whatsapp.com/send?text=${encodedText}`;
    }
  };

  // Fallback URLs
  const getFallbackUrls = (config: WhatsAppConfig): string[] => {
    const encodedText = encodeURIComponent(config.text);
    return [
      `whatsapp://send?text=${encodedText}`, // Deep link mobile
      `https://wa.me/?text=${encodedText}`    // Alternativa wa.me
    ];
  };

  // Copiar para área de transferência
  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      // Fallback para browsers mais antigos
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        return true;
      } catch (fallbackError) {
        console.error('Erro ao copiar para área de transferência:', fallbackError);
        return false;
      }
    }
  };

  // Telemetria (opcional)
  const trackWhatsAppEvent = (event: string, data: WhatsAppTelemetry) => {
    try {
      // Aqui você pode integrar com seu sistema de analytics
      console.log(`WhatsApp Event: ${event}`, data);
      
      // Exemplo de integração com Google Analytics
      if (typeof gtag !== 'undefined') {
        gtag('event', event, {
          custom_parameter_1: data.obraId,
          custom_parameter_2: data.source,
          custom_parameter_3: data.hasPhone,
        });
      }
    } catch (error) {
      console.error('Erro na telemetria:', error);
    }
  };

  // Função principal para abrir WhatsApp
  const openWhatsApp = async (config: WhatsAppConfig, telemetry?: WhatsAppTelemetry) => {
    setIsLoading(true);
    
    try {
      // Track inicial
      if (telemetry) {
        trackWhatsAppEvent('whatsapp_click', telemetry);
      }

      const whatsappUrl = buildWhatsAppUrl(config);
      
      // Tentar abrir em nova aba (deve ser executado no contexto do clique do usuário)
      const newWindow = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      
      if (newWindow && !newWindow.closed) {
        // Sucesso - nova aba aberta
        if (telemetry) {
          trackWhatsAppEvent('whatsapp_opened_newtab', telemetry);
        }
        toast.success('WhatsApp aberto em nova aba!');
      } else {
        // Popup bloqueado - usar fallbacks
        if (telemetry) {
          trackWhatsAppEvent('whatsapp_popup_blocked', telemetry);
        }
        
        await handleFallbacks(config, telemetry);
      }
    } catch (error) {
      console.error('Erro ao abrir WhatsApp:', error);
      await handleFallbacks(config, telemetry);
    } finally {
      setIsLoading(false);
    }
  };

  // Gerenciar fallbacks
  const handleFallbacks = async (config: WhatsAppConfig, telemetry?: WhatsAppTelemetry) => {
    const fallbackUrls = getFallbackUrls(config);
    const whatsappUrl = buildWhatsAppUrl(config);
    
    // Tentar deep link mobile primeiro
    try {
      const deepLinkWindow = window.open(fallbackUrls[0], '_blank', 'noopener,noreferrer');
      
      if (deepLinkWindow && !deepLinkWindow.closed) {
        if (telemetry) {
          trackWhatsAppEvent('whatsapp_fallback_deeplink', telemetry);
        }
        toast.success('WhatsApp aberto via deep link!');
        return;
      }
    } catch (error) {
      console.error('Deep link falhou:', error);
    }

    // Tentar wa.me como segundo fallback
    try {
      const waWindow = window.open(fallbackUrls[1], '_blank', 'noopener,noreferrer');
      
      if (waWindow && !waWindow.closed) {
        if (telemetry) {
          trackWhatsAppEvent('whatsapp_fallback_wame', telemetry);
        }
        toast.success('WhatsApp aberto via wa.me!');
        return;
      }
    } catch (error) {
      console.error('wa.me falhou:', error);
    }

    // Último recurso: copiar link para área de transferência
    const copied = await copyToClipboard(whatsappUrl);
    
    if (copied) {
      if (telemetry) {
        trackWhatsAppEvent('whatsapp_link_copied', telemetry);
      }
      toast.success('Não foi possível abrir o WhatsApp. Link copiado para a área de transferência!');
    } else {
      toast.error('Não foi possível abrir o WhatsApp ou copiar o link. Tente novamente.');
    }
  };

  // Função para abrir WhatsApp com link do RDO (para casos assíncronos)
  const openWhatsAppWithRDOLink = async (
    config: WhatsAppRDOConfig, 
    getRDOLink: () => Promise<string>,
    telemetry?: WhatsAppTelemetry
  ) => {
    setIsLoading(true);

    try {
      // Track inicial
      if (telemetry) {
        trackWhatsAppEvent('whatsapp_click', telemetry);
      }

      // Abrir aba em branco imediatamente (no contexto do clique do usuário)
      const newWindow = window.open('', '_blank', 'noopener,noreferrer');
      
      if (!newWindow || newWindow.closed) {
        // Se não conseguiu abrir aba, usar fallback direto
        const rdoLink = await getRDOLink();
        const fullText = `${config.baseText || 'Informações da obra'} ${rdoLink}`.trim();
        await handleFallbacks({ ...config, text: fullText }, telemetry);
        return;
      }

      try {
        // Gerar link do RDO
        const rdoLink = await getRDOLink();
        const fullText = `${config.baseText || 'Informações da obra'} ${rdoLink}`.trim();
        
        // Construir URL final do WhatsApp
        const finalWhatsAppUrl = buildWhatsAppUrl({ ...config, text: fullText });
        
        // Redirecionar a aba já aberta
        newWindow.location.href = finalWhatsAppUrl;
        
        if (telemetry) {
          trackWhatsAppEvent('whatsapp_opened_newtab', telemetry);
        }
        toast.success('WhatsApp aberto com link do RDO!');
        
      } catch (error) {
        console.error('Erro ao gerar link do RDO:', error);
        newWindow.close();
        
        // Fallback sem link do RDO
        await handleFallbacks({ ...config, text: config.baseText || 'Informações da obra' }, telemetry);
      }
      
    } catch (error) {
      console.error('Erro geral:', error);
      const rdoLink = await getRDOLink().catch(() => '');
      const fullText = `${config.baseText || 'Informações da obra'} ${rdoLink}`.trim();
      await handleFallbacks({ ...config, text: fullText }, telemetry);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    openWhatsApp,
    openWhatsAppWithRDOLink,
    isLoading,
    sanitizePhone,
    buildWhatsAppUrl
  };
};